import Vue from 'vue';
import Vuex from 'vuex';
import { eventBus } from './main';

const API_URL = process.env.VUE_APP_API_URL;
const lastRunDelayInMinutes = Number(
  process.env.VUE_APP_SCHEDULER_DELAY_IN_MINUTES,
);

Vue.use(Vuex);

export default new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    loading: false,
    loggedIn: !!window.localStorage.getItem('token'),
    pausing: false,
    scheduleDelay: lastRunDelayInMinutes,
    scheduling: false,
    medicating: false,
    gettingBlobFile: [],
    scheduleCount: 0,
    clientsWebProcessors: [],
    webProcessorsTypes: [],
  },
  mutations: {
    loading(state) {
      state.loading = true;
    },
    finished(state) {
      state.loading = false;
    },
    loggedIn(state) {
      state.loggedIn = true;
    },
    medicate(state) {
      state.medicating = true;
    },
    medicated(state) {
      state.medicating = false;
    },
    pausing(state) {
      state.pausing = true;
    },
    paused(state) {
      state.pausing = false;
    },
    scheduling(state) {
      state.scheduling = true;
    },
    scheduled(state) {
      state.scheduling = false;
      state.scheduleCount++;
    },
    gettingBlobFile(state, index: number) {
      (state.gettingBlobFile as number[]).push(index);
    },
    gotBlobFile(state, item) {
      const index = (state.gettingBlobFile as number[]).indexOf(item);
      (state.gettingBlobFile as number[]).splice(index, 1);
    },
    setClientsWebProcessors(state, items) {
      state.clientsWebProcessors = items;
    },
    emptyClientsWebProcessors(state) {
      state.clientsWebProcessors = [];
    },
    setWebProcessorsTypes(state, items) {
      state.webProcessorsTypes = items;
    },
    emptyWebProcessorsTypes(state) {
      state.webProcessorsTypes = [];
    },
  },
  actions: {
    async login(store, creds: { username: string; password: string }) {
      store.commit('loading');

      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(creds),
      });

      const data = await response.json();

      if (data.token) {
        window.localStorage.setItem('token', data.token);
        store.commit('loggedIn');
      } else {
        console.error(data);
      }

      store.commit('finished');
    },
    async kill(store, { resourceType, messages }) {
      store.commit('medicate');

      await fetch(
        `${API_URL}/medic?token=${window.localStorage.getItem('token')}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            kill: true,
            resourceType,
            messages,
          }),
        },
      );
      store.commit('medicated');
      eventBus.$emit('failedMessages');
    },
    async killAll(store, { resourceType, messages }) {
      store.commit('medicate');

      await fetch(
        `${API_URL}/medic?token=${window.localStorage.getItem('token')}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            kill: true,
            resourceType,
            messages,
          }),
        },
      );

      store.commit('medicated');
      eventBus.$emit('failedMessages');
    },
    async revive(store, { resourceType, messages }) {
      store.commit('medicate');

      await fetch(
        `${API_URL}/medic?token=${window.localStorage.getItem('token')}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ resourceType, messages }),
        },
      );

      store.commit('medicated');
      eventBus.$emit('failedMessages');
    },
    async reviveAll(store, { resourceType, messages }) {
      store.commit('medicate');

      await fetch(
        `${API_URL}/medic?token=${window.localStorage.getItem('token')}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ resourceType, messages }),
        },
      );

      store.commit('medicated');
      eventBus.$emit('failedMessages');
    },
    async schedule(
      store,
      {
        resourceType,
        modifiedSince,
        modifiedUntil,
        client,
        dataSource,
        disableScheduling,
      },
    ) {
      store.commit('scheduling');

      const url = `${API_URL}/schedule?token=${window.localStorage.getItem(
        'token',
      )}`;

      const request: {
        clientDataTube: {
          client: string;
          dataSource: string;
        };
        modifiedSince: string;
        modifiedUntil?: string;
        resourceType: string;
        disableScheduling: boolean;
      } = {
        clientDataTube: { client, dataSource },
        modifiedSince,
        resourceType,
        disableScheduling,
      };

      if (modifiedUntil) {
        request.modifiedUntil = modifiedUntil;
      }

      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      store.commit('scheduled');
      eventBus.$emit('schedule');
    },
    async pause(store, { client, dataSource, resourceType }) {
      store.commit('pausing');

      const token = window.localStorage.getItem('token');

      const url = `${API_URL}/pause?token=${token}`;

      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client,
          dataSource,
          resourceType,
        }),
      });

      store.commit('paused');
      eventBus.$emit('schedule');
    },
    async getBlobFile(
      store,
      { resourceType, blobname, filename, client, index },
    ) {
      store.commit('gettingBlobFile', index);

      const url = `${API_URL}/get-blob-file`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resourceType,
          client,
          blobname,
        }),
      });

      const blob = await response.blob();
      const fileURL = window.URL.createObjectURL(new Blob([blob]));
      const fileLink = document.createElement('a');
      fileLink.href = fileURL;
      fileLink.setAttribute('download', filename);
      document.body.appendChild(fileLink);
      fileLink.click();
      store.commit('gotBlobFile', index);
    },
  },
});
